// User management ([A]/[P], publisher-scoped server-side): list, create with
// the authType welcome-email matrix, and account ops (resend welcome, reset
// password/2FA, convert auth, de/activate, delete).
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Dialog from '@radix-ui/react-dialog';
import * as Dropdown from '@radix-ui/react-dropdown-menu';
import { toast } from 'sonner';
import { MoreHorizontal, Plus, Search, ShieldCheck, X, Crown } from 'lucide-react';
import { apiFetch, ApiError } from '@/lib/api-client';
import { useAuth } from '@/hooks/useAuth';
import { AppShell } from '@/components/AppShell';
import { formatDate, cn } from '@/lib/utils';
import type { PublisherOption } from '@/pages/publish/Publish';

interface AdminUser {
  id: string;
  email: string;
  displayName: string;
  company: string | null;
  role: 'PLATFORM_ADMIN' | 'VIEWER';
  authType: 'LOCAL' | 'ENTRA_ID' | 'BOTH';
  isActive: boolean;
  languagePref: string;
  totpEnabled: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  publishers: Array<{ publisherId: string; slug: string; name: string; role: string }>;
}

const AUTH_BADGE: Record<AdminUser['authType'], string> = {
  LOCAL: 'bg-slate-50 text-slate-600 border-slate-200',
  ENTRA_ID: 'bg-sky-50 text-sky-700 border-sky-200',
  BOTH: 'bg-violet-50 text-violet-700 border-violet-200',
};

function toggleInSetUsers(set: Set<string>, id: string): Set<string> {
  const next = new Set(set);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

export function Users() {
  const { user: me, isPlatformAdmin } = useAuth();
  const { t, i18n } = useTranslation('admin');

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [convertTarget, setConvertTarget] = useState<AdminUser | null>(null);
  const [grantsTarget, setGrantsTarget] = useState<AdminUser | null>(null);
  const [editTarget, setEditTarget] = useState<AdminUser | null>(null);

  const load = useCallback(async () => {
    try {
      const q = search.trim() ? `&search=${encodeURIComponent(search.trim())}` : '';
      const res = await apiFetch<{ data: AdminUser[] }>(`/admin/users?limit=200${q}`);
      setUsers(res.data);
    } catch {
      toast.error(t('common:error'));
    }
  }, [search, t]);

  useEffect(() => {
    const handle = setTimeout(() => void load(), search ? 250 : 0);
    return () => clearTimeout(handle);
  }, [load, search]);

  async function act(path: string, body: unknown, successMsg: string, method = 'POST') {
    try {
      await apiFetch(path, { method, ...(body !== undefined ? { body: JSON.stringify(body) } : {}) });
      toast.success(successMsg);
      void load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('common:error'));
    }
  }

  function confirmDelete(u: AdminUser) {
    if (window.confirm(t('actions.confirmDelete', { name: u.displayName }))) {
      void act(`/admin/users/${u.id}`, undefined, t('toast.deleted'), 'DELETE');
    }
  }

  return (
    <AppShell>
      <div className="mb-10 animate-fade-up">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-4xl font-medium tracking-tight gradient-text pb-1">{t('title')}</h1>
            <p className="text-sm font-light text-slate-500 mt-1">{t('subtitle')}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('searchPlaceholder')}
                className="paper-input !pl-9 w-72"
              />
            </div>
            <button type="button" onClick={() => setCreateOpen(true)} className="btn-gradient">
              <Plus className="w-4 h-4" />
              {t('create')}
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-slate-200/80 shadow-card overflow-x-auto custom-scrollbar animate-fade-up [animation-delay:0.08s]">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 text-left">
              <th className="px-5 py-3.5 text-xs font-bold text-slate-500">{t('cols.name')}</th>
              <th className="px-3 py-3.5 text-xs font-bold text-slate-500">{t('cols.company')}</th>
              <th className="px-3 py-3.5 text-xs font-bold text-slate-500">{t('cols.auth')}</th>
              <th className="px-3 py-3.5 text-xs font-bold text-slate-500">{t('cols.publishers')}</th>
              <th className="px-3 py-3.5 text-xs font-bold text-slate-500">{t('cols.status')}</th>
              <th className="px-3 py-3.5 text-xs font-bold text-slate-500">{t('cols.twofa')}</th>
              <th className="px-3 py-3.5 text-xs font-bold text-slate-500">{t('cols.lastLogin')}</th>
              <th className="px-3 py-3.5" />
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60 transition-colors">
                <td className="px-5 py-3.5">
                  <div className="flex items-center gap-2">
                    <div className="leading-tight">
                      <p className="font-medium text-ink flex items-center gap-1.5">
                        {u.displayName}
                        {u.role === 'PLATFORM_ADMIN' && (
                          <span title={t('platformAdmin')}>
                            <Crown className="w-3.5 h-3.5 text-brand-orange" />
                          </span>
                        )}
                      </p>
                      <p className="text-xs font-light text-slate-400">{u.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3.5 text-slate-500">{u.company ?? '—'}</td>
                <td className="px-3 py-3.5">
                  <span className={cn('inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold border', AUTH_BADGE[u.authType])}>
                    {u.authType === 'ENTRA_ID' ? 'Microsoft' : u.authType === 'BOTH' ? 'SSO + PW' : 'Password'}
                  </span>
                </td>
                <td className="px-3 py-3.5">
                  <div className="flex flex-wrap gap-1">
                    {u.role === 'PLATFORM_ADMIN' ? (
                      // Platform admins can publish/manage for every publisher,
                      // regardless of any incidental membership — show that, not
                      // just their membership list (which reads as "limited to X").
                      <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-brand-orange/5 text-brand-orange border-brand-orange/20">
                        {t('allPublishers')}
                      </span>
                    ) : (
                      <>
                        {u.publishers.map((p) => (
                          <span
                            key={p.publisherId}
                            className={cn(
                              'inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold border',
                              p.role === 'PUBLISHER_ADMIN'
                                ? 'bg-brand-purple/5 text-brand-purple border-brand-purple/20'
                                : 'bg-slate-50 text-slate-500 border-slate-200',
                            )}
                          >
                            {p.name}
                            {p.role === 'PUBLISHER_ADMIN' ? ' ★' : ''}
                          </span>
                        ))}
                        {u.publishers.length === 0 && <span className="text-slate-300">—</span>}
                      </>
                    )}
                  </div>
                </td>
                <td className="px-3 py-3.5">
                  <span
                    className={cn(
                      'inline-flex items-center gap-1.5 text-xs font-medium',
                      u.isActive ? 'text-emerald-600' : 'text-slate-400',
                    )}
                  >
                    <span className={cn('w-1.5 h-1.5 rounded-full', u.isActive ? 'bg-emerald-500' : 'bg-slate-300')} />
                    {u.isActive ? t('active') : t('inactive')}
                  </span>
                </td>
                <td className="px-3 py-3.5">
                  {u.totpEnabled ? <ShieldCheck className="w-4 h-4 text-emerald-500" /> : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-3 py-3.5 text-slate-500 whitespace-nowrap">
                  {u.lastLoginAt ? formatDate(u.lastLoginAt, i18n.language) : t('never')}
                </td>
                <td className="px-3 py-3.5 text-right">
                  <Dropdown.Root>
                    <Dropdown.Trigger asChild>
                      <button
                        type="button"
                        title={t('actions.menu')}
                        className="p-1.5 rounded-md text-slate-400 hover:text-ink hover:bg-slate-100 transition-colors"
                      >
                        <MoreHorizontal className="w-4 h-4" />
                      </button>
                    </Dropdown.Trigger>
                    <Dropdown.Portal>
                      <Dropdown.Content
                        align="end"
                        sideOffset={4}
                        className="z-50 min-w-[230px] rounded-xl bg-white border border-slate-200 shadow-xl p-1.5 animate-scale-in"
                      >
                        <Item onClick={() => setEditTarget(u)}>{t('actions.edit')}</Item>
                        <Item onClick={() => setGrantsTarget(u)}>{t('actions.manageGrants')}</Item>
                        <Item onClick={() => void act(`/admin/users/${u.id}/resend-welcome`, {}, t('toast.welcomeSent'))}>
                          {t('actions.resendWelcome')}
                        </Item>
                        {u.authType !== 'ENTRA_ID' && (
                          <>
                            <Item onClick={() => void act(`/admin/users/${u.id}/reset-password`, {}, t('toast.resetSent'))}>
                              {t('actions.resetPassword')}
                            </Item>
                            {u.totpEnabled && (
                              <Item onClick={() => void act(`/admin/users/${u.id}/reset-2fa`, {}, t('toast.twofaReset'))}>
                                {t('actions.reset2fa')}
                              </Item>
                            )}
                          </>
                        )}
                        <Item onClick={() => setConvertTarget(u)}>{t('actions.convertAuth')}</Item>
                        <Dropdown.Separator className="my-1 h-px bg-slate-100" />
                        {u.id !== me?.id && (
                          <>
                            <Item
                              onClick={() =>
                                void act(`/admin/users/${u.id}`, { isActive: !u.isActive }, t('toast.updated'), 'PATCH')
                              }
                            >
                              {u.isActive ? t('actions.deactivate') : t('actions.activate')}
                            </Item>
                            <Item destructive onClick={() => confirmDelete(u)}>
                              {t('actions.delete')}
                            </Item>
                          </>
                        )}
                      </Dropdown.Content>
                    </Dropdown.Portal>
                  </Dropdown.Root>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CreateUserDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        isPlatformAdmin={isPlatformAdmin}
        onCreated={() => void load()}
      />

      <ConvertAuthDialog target={convertTarget} onClose={() => setConvertTarget(null)} onDone={() => void load()} />

      <ManageGrantsDialog target={grantsTarget} onClose={() => setGrantsTarget(null)} />

      <EditUserDialog target={editTarget} isPlatformAdmin={isPlatformAdmin} onClose={() => setEditTarget(null)} onDone={() => void load()} />
    </AppShell>
  );
}

function Item({
  children,
  onClick,
  destructive = false,
}: {
  children: React.ReactNode;
  onClick: () => void;
  destructive?: boolean;
}) {
  return (
    <Dropdown.Item
      onSelect={onClick}
      className={cn(
        'px-3 py-2 rounded-lg text-sm cursor-pointer outline-none transition-colors',
        destructive
          ? 'text-rose-600 data-[highlighted]:bg-rose-50'
          : 'text-ink data-[highlighted]:bg-slate-50',
      )}
    >
      {children}
    </Dropdown.Item>
  );
}

// ── Create dialog ─────────────────────────────────────────────

function CreateUserDialog({
  open,
  onOpenChange,
  isPlatformAdmin,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isPlatformAdmin: boolean;
  onCreated: () => void;
}) {
  const { t } = useTranslation('admin');
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [company, setCompany] = useState('');
  const [languagePref, setLanguagePref] = useState('en');
  const [authType, setAuthType] = useState<'LOCAL' | 'ENTRA_ID' | 'BOTH'>('LOCAL');
  const [type, setType] = useState<'REGULAR' | 'PUBLISHER' | 'ADMIN'>('REGULAR');
  // A Publisher may administer ONE OR MORE categories (multi-select).
  const [publisherIds, setPublisherIds] = useState<Set<string>>(new Set());
  const [publishers, setPublishers] = useState<PublisherOption[]>([]);
  const [saving, setSaving] = useState(false);

  // Category list — only needed when an admin creates a "publisher".
  useEffect(() => {
    if (!open || !isPlatformAdmin) return;
    setType('REGULAR');
    setPublisherIds(new Set());
    apiFetch<{ data: PublisherOption[] }>('/pub/publishers')
      .then((res) => {
        setPublishers(res.data);
        if (res.data.length === 1) setPublisherIds(new Set([res.data[0].id])); // auto-pick the only category
      })
      .catch(() => setPublishers([]));
  }, [open, isPlatformAdmin]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const role = type === 'ADMIN' ? 'PLATFORM_ADMIN' : 'VIEWER';
      const publisherAdminIds = type === 'PUBLISHER' ? [...publisherIds] : [];
      await apiFetch('/admin/users', {
        method: 'POST',
        body: JSON.stringify({
          email: email.trim(),
          displayName: displayName.trim(),
          company: company.trim() || undefined,
          languagePref,
          authType,
          ...(isPlatformAdmin ? { role } : {}),
          ...(publisherAdminIds.length > 0 ? { publisherAdminIds } : {}),
        }),
      });
      toast.success(t('dialog.created', { email: email.trim() }));
      onOpenChange(false);
      setEmail('');
      setDisplayName('');
      setCompany('');
      setType('REGULAR');
      setPublisherIds(new Set());
      onCreated();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('common:error'));
    } finally {
      setSaving(false);
    }
  }

  const selectClass = 'paper-input';

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-ink/40 backdrop-blur-sm animate-fade-in" />
        <Dialog.Content className="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[92vw] max-w-lg rounded-2xl bg-white shadow-2xl p-6 animate-dialog-in focus:outline-none max-h-[90dvh] overflow-y-auto custom-scrollbar">
          <div className="flex items-start justify-between gap-4 mb-5">
            <Dialog.Title className="text-base font-semibold text-ink">{t('dialog.createTitle')}</Dialog.Title>
            <Dialog.Close className="p-1 rounded-md text-slate-400 hover:text-ink hover:bg-slate-100 transition-colors">
              <X className="w-4 h-4" />
            </Dialog.Close>
          </div>

          <form onSubmit={(e) => void submit(e)} className="space-y-4">
            <label className="block">
              <span className="block text-xs font-semibold text-slate-500 mb-1.5">{t('dialog.email')}</span>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="paper-input" />
            </label>
            <label className="block">
              <span className="block text-xs font-semibold text-slate-500 mb-1.5">{t('dialog.displayName')}</span>
              <input type="text" required value={displayName} onChange={(e) => setDisplayName(e.target.value)} className="paper-input" />
            </label>
            <div className="grid grid-cols-2 gap-4">
              <label className="block">
                <span className="block text-xs font-semibold text-slate-500 mb-1.5">{t('dialog.company')}</span>
                <input type="text" value={company} onChange={(e) => setCompany(e.target.value)} className="paper-input" />
              </label>
              <label className="block">
                <span className="block text-xs font-semibold text-slate-500 mb-1.5">{t('dialog.language')}</span>
                <select value={languagePref} onChange={(e) => setLanguagePref(e.target.value)} className={selectClass}>
                  <option value="en">English</option>
                  <option value="fr">Français</option>
                  <option value="nl">Nederlands</option>
                </select>
              </label>
            </div>
            <label className="block">
              <span className="block text-xs font-semibold text-slate-500 mb-1.5">{t('dialog.authType')}</span>
              <select value={authType} onChange={(e) => setAuthType(e.target.value as typeof authType)} className={selectClass}>
                <option value="LOCAL">{t('dialog.authTypes.LOCAL')}</option>
                <option value="ENTRA_ID">{t('dialog.authTypes.ENTRA_ID')}</option>
                <option value="BOTH">{t('dialog.authTypes.BOTH')}</option>
              </select>
            </label>
            {isPlatformAdmin && (
              <>
                <label className="block">
                  <span className="block text-xs font-semibold text-slate-500 mb-1.5">{t('dialog.userType')}</span>
                  <select value={type} onChange={(e) => setType(e.target.value as typeof type)} className={selectClass}>
                    <option value="REGULAR">{t('dialog.userTypes.REGULAR')}</option>
                    <option value="PUBLISHER">{t('dialog.userTypes.PUBLISHER')}</option>
                    <option value="ADMIN">{t('dialog.userTypes.ADMIN')}</option>
                  </select>
                </label>
                {type === 'PUBLISHER' && (
                  <div>
                    <span className="block text-xs font-semibold text-slate-500 mb-1.5">
                      {t('dialog.categories')}
                      <span className="font-light text-slate-400 ml-2">{t('dialog.categoriesHint')}</span>
                    </span>
                    {publishers.length === 0 ? (
                      <p className="text-xs font-light text-slate-400">{t('dialog.noCategories')}</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {publishers.map((p) => {
                          const on = publisherIds.has(p.id);
                          return (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => setPublisherIds((s) => toggleInSetUsers(s, p.id))}
                              className={cn(
                                'px-3 py-1.5 rounded-full text-xs font-medium border transition-all',
                                on
                                  ? 'bg-brand-purple text-white border-brand-purple shadow-sm'
                                  : 'bg-white text-slate-600 border-slate-200 hover:border-brand-purple/40 hover:text-brand-purple',
                              )}
                            >
                              {p.name}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <Dialog.Close type="button" className="btn-quiet">
                {t('common:cancel')}
              </Dialog.Close>
              <button type="submit" disabled={saving || !email.trim() || !displayName.trim() || (type === 'PUBLISHER' && publisherIds.size === 0)} className="btn-gradient">
                {saving && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                {saving ? t('dialog.creating') : t('dialog.submit')}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ── Convert-auth dialog ───────────────────────────────────────

function ConvertAuthDialog({
  target,
  onClose,
  onDone,
}: {
  target: AdminUser | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation('admin');
  const [authType, setAuthType] = useState<'LOCAL' | 'ENTRA_ID' | 'BOTH'>('LOCAL');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (target) setAuthType(target.authType === 'LOCAL' ? 'BOTH' : 'LOCAL');
  }, [target]);

  async function submit() {
    if (!target || saving) return;
    setSaving(true);
    try {
      await apiFetch(`/admin/users/${target.id}/convert-auth`, {
        method: 'POST',
        body: JSON.stringify({ authType }),
      });
      toast.success(t('toast.converted'));
      onClose();
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('common:error'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog.Root open={!!target} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-ink/40 backdrop-blur-sm animate-fade-in" />
        <Dialog.Content className="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[92vw] max-w-sm rounded-2xl bg-white shadow-2xl p-6 animate-dialog-in focus:outline-none max-h-[90dvh] overflow-y-auto custom-scrollbar">
          <Dialog.Title className="text-base font-semibold text-ink mb-1">{t('dialog.convertTitle')}</Dialog.Title>
          <Dialog.Description className="text-xs font-light text-slate-500 mb-4">
            {target?.displayName} — {target?.email}
          </Dialog.Description>
          <select
            value={authType}
            onChange={(e) => setAuthType(e.target.value as typeof authType)}
            className="paper-input mb-5"
          >
            {(['LOCAL', 'ENTRA_ID', 'BOTH'] as const)
              .filter((a) => a !== target?.authType)
              .map((a) => (
                <option key={a} value={a}>
                  {t(`dialog.authTypes.${a}`)}
                </option>
              ))}
          </select>
          <div className="flex justify-end gap-3">
            <Dialog.Close className="btn-quiet">{t('common:cancel')}</Dialog.Close>
            <button type="button" onClick={() => void submit()} disabled={saving} className="btn-gradient">
              {saving && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
              {t('dialog.convertSubmit')}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ── Manage-grants dialog (user-centric — plan §4B) ────────────
// Assign collections (incl. the system "Non collection") and one-off direct
// documents to a recipient. Diffs the loaded state and emits POST/DELETE per
// change. Recipients are platform-wide; the targets are scoped server-side to
// what the caller administers.

interface GrantCollection {
  id: string;
  publisherId: string;
  publisherName: string;
  slug: string;
  name: string;
  isDefault: boolean;
  granted: boolean;
}
interface GrantDoc {
  documentId: string;
  title: string;
  publisherId: string;
  publisherName: string;
}
interface UserGrants {
  user: { id: string; displayName: string; email: string; company: string | null };
  collections: GrantCollection[];
  documentGrants: GrantDoc[];
}

function ManageGrantsDialog({ target, onClose }: { target: AdminUser | null; onClose: () => void }) {
  const { t } = useTranslation('admin');
  const [data, setData] = useState<UserGrants | null>(null);
  const [checkedCols, setCheckedCols] = useState<Set<string>>(new Set());
  const [loadedCols, setLoadedCols] = useState<Set<string>>(new Set());
  const [docs, setDocs] = useState<GrantDoc[]>([]);
  const [loadedDocIds, setLoadedDocIds] = useState<Set<string>>(new Set());
  const [docSearch, setDocSearch] = useState('');
  const [allDocs, setAllDocs] = useState<{ id: string; title: string; publisherId: string }[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!target) return;
    setData(null);
    setDocSearch('');
    setAllDocs([]);
    apiFetch<{ data: UserGrants }>(`/admin/users/${target.id}/grants`)
      .then((res) => {
        setData(res.data);
        const granted = new Set(res.data.collections.filter((c) => c.granted).map((c) => c.id));
        setCheckedCols(new Set(granted));
        setLoadedCols(granted);
        setDocs(res.data.documentGrants);
        setLoadedDocIds(new Set(res.data.documentGrants.map((d) => d.documentId)));
      })
      .catch(() => toast.error(t('common:error')));
    // The full grantable (published, in-scope) document set — shown by default.
    apiFetch<{ data: { id: string; title: string; publisherId: string }[] }>('/pub/documents?limit=200&status=PUBLISHED')
      .then((r) => setAllDocs(r.data))
      .catch(() => setAllDocs([]));
  }, [target, t]);

  const collectionsByPublisher = useMemo(() => {
    const map = new Map<string, GrantCollection[]>();
    for (const c of data?.collections ?? []) {
      const arr = map.get(c.publisherName) ?? [];
      arr.push(c);
      map.set(c.publisherName, arr);
    }
    return map;
  }, [data]);

  const docIds = useMemo(() => new Set(docs.map((d) => d.documentId)), [docs]);
  // The full grantable document list, filtered by the search box (shown by
  // default so the operator can pick without typing).
  const filteredDocs = useMemo(() => {
    const q = docSearch.trim().toLowerCase();
    return q ? allDocs.filter((d) => d.title.toLowerCase().includes(q)) : allDocs;
  }, [allDocs, docSearch]);

  function addDoc(d: { id: string; title: string; publisherId: string }) {
    if (docIds.has(d.id)) return;
    setDocs((prev) => [...prev, { documentId: d.id, title: d.title, publisherId: d.publisherId, publisherName: '' }]);
  }

  async function save() {
    if (!target || saving) return;
    setSaving(true);
    try {
      const colAdd = [...checkedCols].filter((id) => !loadedCols.has(id));
      const colRemove = [...loadedCols].filter((id) => !checkedCols.has(id));
      const docAdd = [...docIds].filter((id) => !loadedDocIds.has(id));
      const docRemove = [...loadedDocIds].filter((id) => !docIds.has(id));

      await Promise.all([
        ...colAdd.map((collectionId) =>
          apiFetch('/admin/grants/collection', { method: 'POST', body: JSON.stringify({ userId: target.id, collectionId }) }),
        ),
        ...colRemove.map((collectionId) =>
          apiFetch('/admin/grants/collection', { method: 'DELETE', body: JSON.stringify({ userId: target.id, collectionId }) }),
        ),
        ...docAdd.map((documentId) =>
          apiFetch('/admin/grants/document', { method: 'POST', body: JSON.stringify({ userId: target.id, documentId }) }),
        ),
        ...docRemove.map((documentId) =>
          apiFetch('/admin/grants/document', { method: 'DELETE', body: JSON.stringify({ userId: target.id, documentId }) }),
        ),
      ]);
      toast.success(t('grants.saved'));
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('common:error'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog.Root open={!!target} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-ink/40 backdrop-blur-sm animate-fade-in" />
        <Dialog.Content className="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[92vw] max-w-lg rounded-2xl bg-white shadow-2xl p-6 animate-dialog-in focus:outline-none max-h-[90dvh] overflow-y-auto custom-scrollbar">
          <div className="flex items-start justify-between gap-4 mb-1">
            <Dialog.Title className="text-base font-semibold text-ink">{t('grants.title', { name: target?.displayName ?? '' })}</Dialog.Title>
            <Dialog.Close className="p-1 rounded-md text-slate-400 hover:text-ink hover:bg-slate-100 transition-colors">
              <X className="w-4 h-4" />
            </Dialog.Close>
          </div>
          <Dialog.Description className="text-xs font-light text-slate-500 mb-4">{t('grants.subtitle')}</Dialog.Description>

          {!data ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 rounded-full border-[3px] border-slate-200 border-t-brand-rose animate-spin" />
            </div>
          ) : (
            <>
              <div className="mb-5">
                <h3 className="text-xs font-bold uppercase tracking-[0.14em] text-brand-purple mb-2">{t('grants.collections')}</h3>
                {data.collections.length === 0 ? (
                  <p className="text-sm font-light text-slate-400">{t('grants.noCollections')}</p>
                ) : (
                  <div className="space-y-3 max-h-64 overflow-y-auto custom-scrollbar -mx-1 px-1">
                    {[...collectionsByPublisher.entries()].map(([pubName, cols]) => (
                      <div key={pubName}>
                        {collectionsByPublisher.size > 1 && (
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">{pubName}</p>
                        )}
                        {cols.map((c) => (
                          <label key={c.id} className="flex items-center gap-3 px-2 py-1.5 rounded-lg hover:bg-slate-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={checkedCols.has(c.id)}
                              onChange={() => setCheckedCols((s) => toggleInSetUsers(s, c.id))}
                              className="w-4 h-4 rounded accent-[#ed125f]"
                            />
                            <span className="text-sm text-ink">
                              {c.isDefault ? t('grants.nonCollection') : c.name}
                              {c.isDefault && <span className="text-xs font-light text-slate-400 ml-2">{t('grants.nonCollectionHint')}</span>}
                            </span>
                          </label>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mb-2">
                <h3 className="text-xs font-bold uppercase tracking-[0.14em] text-brand-purple mb-2">{t('grants.documents')}</h3>
                {docs.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {docs.map((d) => (
                      <span key={d.documentId} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                        {d.title}
                        <button type="button" onClick={() => setDocs((prev) => prev.filter((x) => x.documentId !== d.documentId))} className="text-slate-400 hover:text-rose-600">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    value={docSearch}
                    onChange={(e) => setDocSearch(e.target.value)}
                    placeholder={t('grants.searchDocs')}
                    className="paper-input !pl-9"
                  />
                </div>
                <div className="mt-1.5 rounded-lg border border-slate-200 divide-y divide-slate-100 max-h-44 overflow-y-auto custom-scrollbar">
                  {filteredDocs.length === 0 ? (
                    <p className="px-3 py-3 text-sm font-light text-slate-400 text-center">{t('grants.noDocs')}</p>
                  ) : (
                    filteredDocs.map((r) => {
                      const on = docIds.has(r.id);
                      return (
                        <button
                          key={r.id}
                          type="button"
                          onClick={() => addDoc(r)}
                          disabled={on}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-between gap-2"
                        >
                          <span className="truncate">{r.title}</span>
                          {on && <span className="text-[10px] font-semibold text-emerald-600 shrink-0">{t('grants.added')}</span>}
                        </button>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-5">
                <Dialog.Close className="btn-quiet">{t('common:cancel')}</Dialog.Close>
                <button type="button" onClick={() => void save()} disabled={saving} className="btn-gradient">
                  {saving && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                  {t('grants.save')}
                </button>
              </div>
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ── Edit-user dialog (details + type) ─────────────────────────
// Everyone with the Users page may edit details; only an admin may change the
// user's TYPE (Regular / Publisher / Admin) — the API enforces this.

function EditUserDialog({
  target,
  isPlatformAdmin,
  onClose,
  onDone,
}: {
  target: AdminUser | null;
  isPlatformAdmin: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation('admin');
  const [displayName, setDisplayName] = useState('');
  const [company, setCompany] = useState('');
  const [languagePref, setLanguagePref] = useState('en');
  const [type, setType] = useState<'REGULAR' | 'PUBLISHER' | 'ADMIN'>('REGULAR');
  const [origType, setOrigType] = useState<'REGULAR' | 'PUBLISHER' | 'ADMIN'>('REGULAR');
  // A Publisher may administer ONE OR MORE categories (multi-select).
  const [publisherIds, setPublisherIds] = useState<Set<string>>(new Set());
  const [origDeptIds, setOrigDeptIds] = useState<Set<string>>(new Set());
  const [publishers, setPublishers] = useState<PublisherOption[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!target) return;
    setDisplayName(target.displayName);
    setCompany(target.company ?? '');
    setLanguagePref(target.languagePref || 'en');
    const ct = target.role === 'PLATFORM_ADMIN' ? 'ADMIN' : target.publishers.some((p) => p.role === 'PUBLISHER_ADMIN') ? 'PUBLISHER' : 'REGULAR';
    const depts = target.publishers.filter((p) => p.role === 'PUBLISHER_ADMIN').map((p) => p.publisherId);
    setType(ct);
    setOrigType(ct);
    setPublisherIds(new Set(depts));
    setOrigDeptIds(new Set(depts));
    if (isPlatformAdmin) {
      apiFetch<{ data: PublisherOption[] }>('/pub/publishers')
        .then((r) => setPublishers(r.data))
        .catch(() => setPublishers([]));
    }
  }, [target, isPlatformAdmin]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!target || saving) return;
    setSaving(true);
    try {
      const body: Record<string, unknown> = {
        displayName: displayName.trim(),
        company: company.trim() || null,
        languagePref,
      };
      if (isPlatformAdmin) {
        body.role = type === 'ADMIN' ? 'PLATFORM_ADMIN' : 'VIEWER';
        // Only touch memberships when the type/categories actually changed, so
        // editing details never reduces a multi-category publisher.
        const deptsChanged =
          publisherIds.size !== origDeptIds.size || [...publisherIds].some((id) => !origDeptIds.has(id));
        const membershipChanged = type !== origType || (type === 'PUBLISHER' && deptsChanged);
        if (membershipChanged) {
          body.publisherAdminIds = type === 'PUBLISHER' ? [...publisherIds] : [];
        }
      }
      await apiFetch(`/admin/users/${target.id}`, { method: 'PATCH', body: JSON.stringify(body) });
      toast.success(t('toast.updated'));
      onClose();
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('common:error'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog.Root open={!!target} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-ink/40 backdrop-blur-sm animate-fade-in" />
        <Dialog.Content className="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[92vw] max-w-lg rounded-2xl bg-white shadow-2xl p-6 animate-dialog-in focus:outline-none max-h-[90dvh] overflow-y-auto custom-scrollbar">
          <div className="flex items-start justify-between gap-4 mb-1">
            <Dialog.Title className="text-base font-semibold text-ink">{t('dialog.editTitle')}</Dialog.Title>
            <Dialog.Close className="p-1 rounded-md text-slate-400 hover:text-ink hover:bg-slate-100 transition-colors">
              <X className="w-4 h-4" />
            </Dialog.Close>
          </div>
          <p className="text-xs font-light text-slate-400 mb-4">{target?.email}</p>

          <form onSubmit={(e) => void save(e)} className="space-y-4">
            <label className="block">
              <span className="block text-xs font-semibold text-slate-500 mb-1.5">{t('dialog.displayName')}</span>
              <input type="text" required value={displayName} onChange={(e) => setDisplayName(e.target.value)} className="paper-input" />
            </label>
            <div className="grid grid-cols-2 gap-4">
              <label className="block">
                <span className="block text-xs font-semibold text-slate-500 mb-1.5">{t('dialog.company')}</span>
                <input type="text" value={company} onChange={(e) => setCompany(e.target.value)} className="paper-input" />
              </label>
              <label className="block">
                <span className="block text-xs font-semibold text-slate-500 mb-1.5">{t('dialog.language')}</span>
                <select value={languagePref} onChange={(e) => setLanguagePref(e.target.value)} className="paper-input">
                  <option value="en">English</option>
                  <option value="fr">Français</option>
                  <option value="nl">Nederlands</option>
                </select>
              </label>
            </div>
            {isPlatformAdmin && (
              <>
                <label className="block">
                  <span className="block text-xs font-semibold text-slate-500 mb-1.5">{t('dialog.userType')}</span>
                  <select value={type} onChange={(e) => setType(e.target.value as typeof type)} className="paper-input">
                    <option value="REGULAR">{t('dialog.userTypes.REGULAR')}</option>
                    <option value="PUBLISHER">{t('dialog.userTypes.PUBLISHER')}</option>
                    <option value="ADMIN">{t('dialog.userTypes.ADMIN')}</option>
                  </select>
                </label>
                {type === 'PUBLISHER' && (
                  <div>
                    <span className="block text-xs font-semibold text-slate-500 mb-1.5">
                      {t('dialog.categories')}
                      <span className="font-light text-slate-400 ml-2">{t('dialog.categoriesHint')}</span>
                    </span>
                    {publishers.length === 0 ? (
                      <p className="text-xs font-light text-slate-400">{t('dialog.noCategories')}</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {publishers.map((p) => {
                          const on = publisherIds.has(p.id);
                          return (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => setPublisherIds((s) => toggleInSetUsers(s, p.id))}
                              className={cn(
                                'px-3 py-1.5 rounded-full text-xs font-medium border transition-all',
                                on
                                  ? 'bg-brand-purple text-white border-brand-purple shadow-sm'
                                  : 'bg-white text-slate-600 border-slate-200 hover:border-brand-purple/40 hover:text-brand-purple',
                              )}
                            >
                              {p.name}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
            <div className="flex justify-end gap-3 pt-1">
              <Dialog.Close type="button" className="btn-quiet">
                {t('common:cancel')}
              </Dialog.Close>
              <button
                type="submit"
                disabled={saving || !displayName.trim() || (isPlatformAdmin && type === 'PUBLISHER' && publisherIds.size === 0)}
                className="btn-gradient"
              >
                {saving && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                {t('dialog.saveChanges')}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
